import { useState, useCallback, useRef } from 'react'

export const useOptimisticUpdates = () => {
  const [deletingFiles, setDeletingFiles] = useState(new Set())
  const [uploadingFiles, setUploadingFiles] = useState(new Map())
  const timeoutRefs = useRef(new Map())

  // Add a file to the deleting state
  const addDeletingFile = useCallback((fileId) => {
    setDeletingFiles((prev) => new Set([...prev, fileId]))

    // Auto-clear after 30 seconds in case of API issues
    const timeoutId = setTimeout(() => {
      setDeletingFiles((prev) => {
        const newSet = new Set(prev)
        newSet.delete(fileId)
        return newSet
      })
      timeoutRefs.current.delete(fileId)
    }, 30000)

    timeoutRefs.current.set(fileId, timeoutId)
  }, [])

  // Remove a file from the deleting state
  const removeDeletingFile = useCallback((fileId) => {
    setDeletingFiles((prev) => {
      const newSet = new Set(prev)
      newSet.delete(fileId)
      return newSet
    })

    // Clear timeout
    const timeoutId = timeoutRefs.current.get(fileId)
    if (timeoutId) {
      clearTimeout(timeoutId)
      timeoutRefs.current.delete(fileId)
    }
  }, [])

  // Add an uploading file with progress
  const addUploadingFile = useCallback((tempId, fileInfo) => {
    setUploadingFiles(
      (prev) =>
        new Map(
          prev.set(tempId, {
            ...fileInfo,
            progress: 0,
            status: 'uploading',
          }),
        ),
    )
  }, [])

  // Update upload progress
  const updateUploadProgress = useCallback((tempId, progress) => {
    setUploadingFiles((prev) => {
      const newMap = new Map(prev)
      const existing = newMap.get(tempId)
      if (existing) {
        newMap.set(tempId, { ...existing, progress })
      }
      return newMap
    })
  }, [])

  // Complete upload (remove from uploading state)
  const completeUpload = useCallback((tempId) => {
    setUploadingFiles((prev) => {
      const newMap = new Map(prev)
      newMap.delete(tempId)
      return newMap
    })
  }, [])

  // Fail upload
  const failUpload = useCallback((tempId, error) => {
    setUploadingFiles((prev) => {
      const newMap = new Map(prev)
      const existing = newMap.get(tempId)
      if (existing) {
        newMap.set(tempId, {
          ...existing,
          status: 'failed',
          error,
        })
      }
      return newMap
    })

    // Auto-remove failed uploads after 10 seconds
    setTimeout(() => {
      setUploadingFiles((prev) => {
        const newMap = new Map(prev)
        newMap.delete(tempId)
        return newMap
      })
    }, 10000)
  }, [])

  // Filter out deleting files from data
  const filterDeletingFiles = useCallback(
    (data) => {
      const list = Array.isArray(data) ? data : []
      return list.filter((file) => {
        const fileId =
          file.ont_bam_id || file.pacbio_bam_id || file.vcf_id || file.fastq_pair_id || file.fastq_file_id || file.id
        return !deletingFiles.has(fileId)
      })
    },
    [deletingFiles],
  )

  // Add uploading files to data as temporary entries
  const addUploadingToData = useCallback(
    (data) => {
      const uploadingArray = Array.from(uploadingFiles.values()).map((uploadInfo, index) => ({
        id: `temp-${uploadInfo.tempId}`,
        name: uploadInfo.fileName,
        sample_name: uploadInfo.sampleName || 'Uploading...',
        created_at: new Date().toISOString(),
        isUploading: true,
        uploadProgress: uploadInfo.progress,
        uploadStatus: uploadInfo.status,
        uploadError: uploadInfo.error,
        // Add temp identifier to avoid conflicts
        tempId: uploadInfo.tempId,
      }))

      const base = Array.isArray(data) ? data : []
      return [...uploadingArray, ...base]
    },
    [uploadingFiles],
  )

  // Clear all optimistic states (useful for reset)
  const clearOptimisticStates = useCallback(() => {
    setDeletingFiles(new Set())
    setUploadingFiles(new Map())

    // Clear all timeouts
    timeoutRefs.current.forEach((timeoutId) => clearTimeout(timeoutId))
    timeoutRefs.current.clear()
  }, [])

  return {
    // State
    deletingFiles,
    uploadingFiles,

    // Actions
    addDeletingFile,
    removeDeletingFile,
    addUploadingFile,
    updateUploadProgress,
    completeUpload,
    failUpload,

    // Data modifiers
    filterDeletingFiles,
    addUploadingToData,

    // Utilities
    clearOptimisticStates,
    isFileDeleting: useCallback((fileId) => deletingFiles.has(fileId), [deletingFiles]),
    getUploadingCount: useCallback(() => uploadingFiles.size, [uploadingFiles]),
  }
}
